import React, { useState, useRef, useEffect } from 'react'
import { Image } from 'antd'
import styles from './style.module.scss'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import ClassName from 'clsx'
import { Flex } from 'antd'
import { navItems } from './data'

interface ProductGroup {
  category: string
  products: { name: string; image?: string; link?: string }[]
}

interface SubMenuContent {
  leftTitle?: string
  leftTabs?: { label: string; key: string }[]
  centerByTab?: Record<string, ProductGroup[]>
  rightByTab?: Record<string, string[]>
}

interface NavItem {
  label: string
  key: string
  link?: string
  submenu?: SubMenuContent
}

interface NavMenuProps {
  logo: React.ReactNode
  extra?: React.ReactNode
  items: NavItem[]
  panelTopOffset?: number
  className?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

function openUrl(path: string, navigate: any) {
  if (!path) return
  return path.startsWith('http') ? window.open(path, '_blank') : navigate?.(path)
}

function ProductItem({
  name,
  image,
  path,
}: { name: string; image?: string; path?: string } & React.HTMLAttributes<HTMLDivElement>) {
  const navigate = useNavigate()
  return (
    <Flex
      align='center'
      className={ClassName(styles.productItem, path && styles.productItemLink)}
      onClick={() => openUrl(path, navigate)}
    >
      {image && <Image preview={false} src={image} alt='' rootClassName={styles.productImage} />}
      <div>{name}</div>
    </Flex>
  )
}

const SubMenuPanel: React.FC<{ content: SubMenuContent }> = ({ content }) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string | null>()

  useEffect(() => {
    setActiveTab(content.leftTabs?.[0]?.key || null)
  }, [content])

  const productGroups = Array.isArray(content.centerByTab)
    ? content.centerByTab
    : activeTab
    ? content.centerByTab?.[activeTab] || []
    : []
  const scenes = activeTab ? content.rightByTab?.[activeTab] || [] : []

  return (
    <div className={styles.subMenuPanelWrapper}>
      <div className={styles.leftCol}>
        {content.leftTitle && <div className={styles.sectionTitle}>{content.leftTitle}</div>}
        <ul>
          {content.leftTabs?.map(tab => (
            <li
              key={tab.key}
              className={`${styles.subItem} ${activeTab === tab.key ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.centerCol}>
        {productGroups.map((group, idx) => (
          <Flex vertical key={idx} className={styles.productGroup}>
            <div className={styles.productCategory}>{group.category}</div>
            <Flex className={styles.productList}>
              {group?.products?.map((p, idx2) => (
                <ProductItem key={idx2} name={p.name} image={p.image} path={p.path} />
              ))}
            </Flex>
          </Flex>
        ))}
      </div>

      {!!scenes?.length && (
        <div className={styles.rightCol}>
          <div className={styles.sectionTitle}>Solution & Scene</div>
          <ul>
            {scenes.map((item, idx) => (
              <li key={idx} className={styles.subItem} onClick={() => openUrl(item.path, navigate)}>
                {item.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const NavMenu: React.FC<NavMenuProps> = ({
  soul,
  logo,
  extra,
  items = navItems.filter(item => item.link !== '/contact'),
  panelTopOffset = 0,
  className,
  onMouseEnter,
  onMouseLeave,
}) => {
  const navigate = useNavigate()
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const urlParams = useParams()

  const handleMouseEnter = (key: string) => {
    setOpenKey(key)
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (menuRef.current && e.relatedTarget) {
      try {
        if (!menuRef.current.contains(e.relatedTarget as Node)) {
          setOpenKey(null)
        }
      } catch (error) {
        setOpenKey(null)
      }
    }
  }

  // 展开菜单的时候，如果路由变化，则关闭菜单
  useEffect(() => {
    if (openKey) {
      setOpenKey(null)
    }
  }, [urlParams])

  return (
    <div
      className={ClassName(styles.navWrapper, className, soul && styles.soul)}
      ref={menuRef}
      onMouseLeave={e => {
        handleMouseLeave?.(e)
        onMouseLeave?.(e)
      }}
    >
      <div className={styles.logo}>{logo}</div>
      <ul className={styles.menu}>
        {items.map(item => (
          <li
            key={item.key}
            className={`${styles.menuItem} ${openKey === item.key ? styles.menuItemHover : ''}`}
            onMouseEnter={e => {
              handleMouseEnter(item.key)
              onMouseEnter?.(e)
            }}
            onClick={() => {
              setActiveKey(item.key)
              if (['home', 'about-us', 'news', 'carus-ai'].includes(item?.key) && item.link) {
                openUrl(item.link, navigate)
              }
            }}
          >
            <span className={activeKey === item.key ? styles.active : styles.inactive}>{item.label}</span>
          </li>
        ))}
      </ul>

      <div className={styles.extra}>{extra}</div>

      <AnimatePresence>
        {openKey && items.find(i => i.key === openKey)?.submenu && (
          <motion.div
            className={styles.subMenuFullWidth}
            style={{ top: `calc(100% + ${panelTopOffset}px)` }}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <SubMenuPanel content={items.find(i => i.key === openKey)!.submenu!} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NavMenu
